<template>
  <transition name="modal">
    <div v-if="isVisible" class="modal-mask" @click.self="closeModal">
      <div class="modal-container" ref="modalContainer">
        <div class="modal-content">
          <!-- 头部 -->
          <div class="sort-header">
            <div class="header-left">
              <i class="fas fa-images"></i>
              <span>精选时光图片排序 (拖拽调整)</span>
              <span class="badge">{{ imageList.length }} 张</span>
            </div>
            <div class="header-right">
                <button class="btn btn-close-bottom" @click="closeModal">
              <i class="fas fa-times"></i> 关闭
            </button>
              <button class="btn btn-outline" @click="resetOrder">
                <i class="fas fa-undo-alt"></i> 重置
              </button>
              <button class="btn btn-primary" @click="handleSave" :disabled="isSaving">
                <i class="fas fa-save"></i> {{ isSaving ? '保存中...' : '保存排序' }}
              </button>
              <button class="btn-close" @click="closeModal">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- 图片网格：每行6列 -->
          <div class="image-grid">
            <div
              v-for="(item, index) in imageList"
              :key="item.id"
              class="grid-item"
              :class="{ 'dragging': dragState.draggedIndex === index }"
              :draggable="true"
              @dragstart="onDragStart($event, index)"
              @dragend="onDragEnd"
              @dragover="onDragOver($event, index)"
              @drop="onDrop($event, index)"
              @dragenter="onDragEnter($event, index)"
            >
              <div class="image-card">
                <img 
                  :src="baseFileUrl + item.img" 
                  :alt="'图片-' + item.id" 
                  loading="lazy"
                  @dragstart.prevent
                  draggable="false"
                  style="user-drag: none; user-select: none; -webkit-user-drag: none;"
                />
                <div class="order-badge">{{ index + 1 }}</div>
                <div class="drag-handle">
                  <i class="fas fa-grip-vertical"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div v-if="!imageList.length" class="empty-state">
            <i class="fas fa-inbox"></i>
            <p>暂无图片数据</p>
          </div>

          <!-- 底部信息 -->
          <div class="footer-info">
            <span>当前排序 IDs：</span>
            <span class="order-preview">{{ orderPreview }}</span>
          </div>

          <!-- ✅ 新增：底部关闭按钮 -->
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import config from '@/utils/Config';

export default {
  name: 'ImageSortGrid',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      baseFileUrl: config.BASE_FILE_URL,
      imageList: [],
      dragState: {
        draggedIndex: null,
        enterIndex: null,
      },
      isSaving: false,
      originalList: [],
      // 滚动相关
      scrollTimer: null,
      scrollSpeed: 8,
    };
  },
  computed: {
    orderPreview() {
      return this.imageList.map(item => item.id).join(' → ');
    },
    sortPayload() {
      return this.imageList.map((item, index) => ({
        id: item.id,
        showSort: index + 1,
      }));
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length) {
          const sorted = [...newVal].sort((a, b) => (a.showSort || 0) - (b.showSort || 0));
          this.imageList = sorted;
          this.originalList = sorted.map(item => ({ ...item }));
        } else {
          this.imageList = [];
          this.originalList = [];
        }
      }
    }
  },
  methods: {
    // ----- 弹窗控制 -----
       closeModal() {
      this.isVisible = !this.isVisible
      this.$emit('update:visible', false);
    },

    // ----- 拖拽事件 -----
    onDragStart(e, index) {
      this.dragState.draggedIndex = index;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
      
      this.$nextTick(() => {
        e.target.style.opacity = '0.5';
      });
    },
    
    onDragEnd(e) {
      this.dragState.draggedIndex = null;
      this.dragState.enterIndex = null;
      e.target.style.opacity = '1';
      const items = this.$el.querySelectorAll('.grid-item');
      items.forEach(el => el.classList.remove('drag-over'));
      this.stopScroll();
    },
    
    onDragOver(e, index) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const draggedIdx = this.dragState.draggedIndex;
      
      if (draggedIdx !== null && draggedIdx !== index) {
        const items = this.$el.querySelectorAll('.grid-item');
        items.forEach(el => el.classList.remove('drag-over'));
        const targetEl = e.currentTarget;
        if (targetEl) targetEl.classList.add('drag-over');
        this.dragState.enterIndex = index;
      }
      
      this.handleAutoScroll(e);
    },
    
    onDragEnter(e, index) {
      e.preventDefault();
    },
    
    onDrop(e, targetIndex) {
      e.preventDefault();
      const items = this.$el.querySelectorAll('.grid-item');
      items.forEach(el => el.classList.remove('drag-over'));

      const draggedIdx = this.dragState.draggedIndex;
      if (draggedIdx === null || draggedIdx === targetIndex) {
        this.dragState.draggedIndex = null;
        return;
      }

      const list = [...this.imageList];
      const [removed] = list.splice(draggedIdx, 1);
      list.splice(targetIndex, 0, removed);
      this.imageList = list;

      this.dragState.draggedIndex = null;
      this.dragState.enterIndex = null;

      this.emitUpdate();
      this.stopScroll();
    },

    // ----- 自动滚动方法 -----
    handleAutoScroll(e) {
      const container = this.$refs.modalContainer;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const mouseY = e.clientY;
      const threshold = 60;
      
      this.stopScroll();
      
      const isNearTop = mouseY - rect.top < threshold && mouseY > rect.top;
      const isNearBottom = rect.bottom - mouseY < threshold && mouseY < rect.bottom;
      
      if (isNearTop) {
        const distance = threshold - (mouseY - rect.top);
        const speed = -Math.max(2, distance / 4);
        this.startScroll(container, speed);
      } else if (isNearBottom) {
        const distance = threshold - (rect.bottom - mouseY);
        const speed = Math.max(2, distance / 4);
        this.startScroll(container, speed);
      }
    },
    
    startScroll(container, speed) {
      const step = () => {
        if (!container) return;
        
        const maxScroll = container.scrollHeight - container.clientHeight;
        const newScrollTop = container.scrollTop + speed;
        
        if (newScrollTop <= 0) {
          container.scrollTop = 0;
          this.stopScroll();
          return;
        }
        if (newScrollTop >= maxScroll) {
          container.scrollTop = maxScroll;
          this.stopScroll();
          return;
        }
        
        container.scrollTop = newScrollTop;
        this.scrollTimer = requestAnimationFrame(step);
      };
      this.scrollTimer = requestAnimationFrame(step);
    },
    
    stopScroll() {
      if (this.scrollTimer) {
        cancelAnimationFrame(this.scrollTimer);
        this.scrollTimer = null;
      }
    },

    // ----- 重置 -----
    resetOrder() {
      if (this.originalList.length) {
        this.imageList = this.originalList.map(item => ({ ...item }));
        this.emitUpdate();
      } else {
        const sorted = [...this.value].sort((a, b) => (a.showSort || 0) - (b.showSort || 0));
        this.imageList = sorted;
        this.originalList = sorted.map(item => ({ ...item }));
        this.emitUpdate();
      }
    },

    // ----- 保存 -----
    handleSave() {
      if (this.isSaving) return;
      this.$emit('save', this.sortPayload);
      this.isSaving = true;
    },

    // ----- 父页面保存完成后调用 -----
    doneSaving(success = true) {
      this.isSaving = false;
      if (success) {
        this.originalList = this.imageList.map(item => ({ ...item }));
        const updatedValue = this.imageList.map((item, idx) => ({
          ...item,
          showSort: idx + 1
        }));
        this.$emit('input', updatedValue);
        this.closeModal();
      }
    },

    // ----- 通知父组件数据变化 -----
    emitUpdate() {
      const updated = this.imageList.map((item, idx) => ({
        ...item,
        showSort: idx + 1
      }));
      this.$emit('input', updated);
      this.$emit('change', updated);
    },

    // ----- 对外暴露方法 -----
    getSortData() {
      return this.sortPayload;
    }
  }
};
</script>

<style scoped>
/* 弹窗遮罩 */
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
}

.modal-container {
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  padding: 24px 28px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

/* 滚动条美化 */
.modal-container::-webkit-scrollbar {
  width: 6px;
}

.modal-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modal-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 10px;
}

.modal-container::-webkit-scrollbar-thumb:hover {
  background: #a8b0b8;
}

.modal-content {
  display: flex;
  flex-direction: column;
}

/* 头部 */
.sort-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  font-size: 1.1rem;
  color: #1e293b;
}

.header-left i {
  color: #3b82f6;
  font-size: 1.2rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.badge {
  background: #eef2f6;
  padding: 2px 12px;
  border-radius: 30px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 40px;
  border: none;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  background: #f1f5f9;
  color: #1e293b;
}

.btn i {
  font-size: 0.9rem;
}

.btn-outline {
  background: transparent;
  border: 1px solid #d1d9e6;
}

.btn-outline:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.2);
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* ✅ 底部关闭按钮样式 */
.footer-actions {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
}

.btn-close-bottom {
  background: #f1f5f9;
  color: #64748b;
  padding: 10px 32px;
  font-size: 0.95rem;
}

.btn-close-bottom:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.btn-close-bottom i {
  margin-right: 6px;
}

/* 图片网格 - 每行6列 */
.image-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin: 8px 0 16px;
  min-height: 180px;
}

.grid-item {
  aspect-ratio: 1 / 1;
  cursor: grab;
  border-radius: 16px;
  transition: transform 0.15s, box-shadow 0.2s, border-color 0.2s;
  position: relative;
  border: 2px solid transparent;
  background: #fafcff;
}

.grid-item:active {
  cursor: grabbing;
}

.grid-item.dragging {
  opacity: 0.6;
  transform: scale(0.96);
  border-color: #3b82f6;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.15);
  z-index: 10;
}

.grid-item.drag-over {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: scale(1.02);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.12);
}

.image-card {
  width: 100%;
  height: 100%;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  background: #f1f5f9;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
}

.image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: filter 0.2s;
  background: #e9edf2;
  user-drag: none;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  pointer-events: none;
}

.order-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(2px);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.drag-handle {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  opacity: 0.7;
  transition: opacity 0.2s;
  pointer-events: none;
}

.grid-item:hover .drag-handle {
  opacity: 1;
}

.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
  background: #f8fafc;
  border-radius: 24px;
  border: 2px dashed #d1d9e6;
}

.empty-state i {
  font-size: 2.6rem;
  margin-bottom: 12px;
  color: #cbd5e1;
}

.footer-info {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #f0f2f5;
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.order-preview {
  background: #f1f5f9;
  padding: 2px 14px;
  border-radius: 40px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  color: #1e293b;
  letter-spacing: 0.3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;
}

.grid-item:hover .image-card img {
  filter: brightness(1.02);
  transform: scale(1.01);
}
.grid-item .image-card img {
  transition: transform 0.2s, filter 0.2s;
}

/* 过渡动画 */
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9);
}

/* 响应式 */
@media (max-width: 820px) {
  .image-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .modal-container {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .image-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  .modal-container {
    padding: 12px;
  }
}
</style>